import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { FileText, Loader2, RefreshCw, Wallet, Building2 } from "lucide-react";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function AnaliseComissoesModal({ open, onClose }) {
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [unidades, setUnidades] = useState([]);
  const [unidade, setUnidade] = useState("__todas__");
  const [status, setStatus] = useState("__todos__");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/comercial/unidades").then((r) => setUnidades(r.data || [])).catch(() => setUnidades([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    const params = { mes, ano };
    if (unidade !== "__todas__") params.unidade_id = unidade;
    if (status !== "__todos__") params.status_pagamento = status;
    api.get("/comercial/comissoes/analise-pagamento", { params })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, mes, ano, unidade, status]);

  const openPDF = (vendedorId) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("lab_token");
    // Abre nova aba com Authorization via fetch + blob URL
    fetch(`${base}/api/comercial/comissoes/${vendedorId}/pdf?mes=${mes}&ano=${ano}&formato=pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="analise-comissoes-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "#1B3A6B" }}>
            <Wallet size={20} /> Análise de Pagamento por Vendedor
          </DialogTitle>
          <DialogDescription>
            Visão consolidada de comissões por vendedor, unidade e período. Acesso: Financeiro / Diretor / Supervisão / Gerência.
          </DialogDescription>
        </DialogHeader>

        {/* Filtros */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
          <div>
            <Label className="text-xs uppercase">Mês</Label>
            <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
              <SelectTrigger data-testid="filtro-mes"><SelectValue /></SelectTrigger>
              <SelectContent>{MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Ano</Label>
            <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
              <SelectTrigger data-testid="filtro-ano"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[hoje.getFullYear() - 1, hoje.getFullYear(), hoje.getFullYear() + 1].map((a) => (
                  <SelectItem key={a} value={String(a)}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Unidade</Label>
            <Select value={unidade} onValueChange={setUnidade}>
              <SelectTrigger data-testid="filtro-unidade"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todas__">Todas</SelectItem>
                {unidades.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.nome} ({u.estado})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="filtro-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__todos__">Todos</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="a_receber">A receber</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Totais */}
        {data && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <Card className="p-3"><div className="text-[10px] uppercase text-muted-foreground">Vendedores</div><div className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{data.totais.vendedores}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase text-muted-foreground">Contratos</div><div className="text-2xl font-bold" style={{ color: "#1B3A6B" }}>{data.totais.contratos}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase text-muted-foreground">Bruto</div><div className="text-xl font-bold" style={{ color: "#1B3A6B" }}>{fmt(data.totais.bruto)}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase text-green-700">Pago</div><div className="text-xl font-bold text-green-700">{fmt(data.totais.pago)}</div></Card>
            <Card className="p-3"><div className="text-[10px] uppercase text-amber-700">A receber</div><div className="text-xl font-bold text-amber-700">{fmt(data.totais.pendente)}</div></Card>
          </div>
        )}

        {/* Tabela agrupada */}
        <Card className="mt-3">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendedor</TableHead>
                  <TableHead>Unidade</TableHead>
                  <TableHead className="text-center">Contratos</TableHead>
                  <TableHead className="text-right">Bruto</TableHead>
                  <TableHead className="text-right">Pago</TableHead>
                  <TableHead className="text-right">A receber</TableHead>
                  <TableHead className="text-center">PDF</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.items?.map((v) => (
                  <TableRow key={v.vendedor_id} data-testid={`linha-${v.vendedor_id}`}>
                    <TableCell>
                      <div className="font-medium">{v.vendedor_nome}</div>
                      <div className="text-[11px] text-muted-foreground">{v.vendedor_email} · {v.vendedor_role}</div>
                    </TableCell>
                    <TableCell><Badge variant="outline" className="gap-1"><Building2 size={11} />{v.unidade_nome || "—"} {v.unidade_estado && <span className="text-[10px] opacity-70">({v.unidade_estado})</span>}</Badge></TableCell>
                    <TableCell className="text-center">{v.total_contratos}</TableCell>
                    <TableCell className="text-right font-bold" style={{ color: "#1B3A6B" }}>{fmt(v.total_bruto)}</TableCell>
                    <TableCell className="text-right text-green-700">{fmt(v.pago)}</TableCell>
                    <TableCell className="text-right text-amber-700">{fmt(v.pendente)}</TableCell>
                    <TableCell className="text-center">
                      <Button size="sm" variant="outline" onClick={() => openPDF(v.vendedor_id)} data-testid={`pdf-${v.vendedor_id}`}>
                        <FileText size={14} className="mr-1" /> PDF
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {(!data?.items || data.items.length === 0) && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Nenhuma comissão neste período.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </Card>

        <div className="flex justify-end mt-3">
          <Button variant="ghost" onClick={() => { setMes(hoje.getMonth() + 1); setAno(hoje.getFullYear()); setUnidade("__todas__"); setStatus("__todos__"); }}>
            <RefreshCw size={14} className="mr-1" /> Limpar filtros
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
